// Boas-vindas à Gestão de Estoque: tutorial em 5 etapas (as mesmas que organizam a lateral —
// etapas.js). Mostra onde o estoque está hoje (abertura feita? meses fechados?) e abre a etapa
// atual; cada etapa tem os passos e o atalho para as telas.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { getEstoqueAbertura, getEstoqueContexto, getEstoqueFornecedores, getRoleFromToken } from "../../api";
import EstoqueLayout from "./EstoqueLayout";
import { etapasVisiveis } from "./etapas";
import { palette, useIsMobile } from "./ui";

/** Situação de cada etapa a partir do que já existe no estoque. */
function situacoes(info) {
  if (!info) return {};
  const { contexto, abertura, fornecedores } = info;
  const aberto = contexto.abertura_concluida;
  const fechados = contexto.periodos.filter((p) => p.status === "FECHADO").length;
  return {
    preparar: fornecedores > 0 || aberto ? { estado: "feito", texto: `${fornecedores} fornecedor(es) cadastrado(s)` } : { estado: "atual", texto: "Comece por aqui" },
    abrir: aberto
      ? { estado: "feito", texto: `Estoque aberto em ${contexto.data_abertura.split("-").reverse().join("/")}` }
      : abertura?.status === "ABERTO"
        ? { estado: "atual", texto: `Vistoria em andamento · ${abertura.resumo.contados} de ${abertura.resumo.total} contados` }
        : { estado: fornecedores > 0 ? "atual" : "pendente", texto: "Ainda não feita" },
    movimentar: aberto ? { estado: "rotina", texto: "Rotina do dia a dia" } : { estado: "pendente", texto: "Libera depois da abertura" },
    fechar: aberto ? { estado: "rotina", texto: fechados ? `${fechados} mês(es) fechado(s)` : "Todo fim de mês" } : { estado: "pendente", texto: "Libera depois da abertura" },
    acompanhar: aberto ? { estado: "rotina", texto: "Sempre disponível" } : { estado: "pendente", texto: "Libera depois da abertura" },
  };
}

function Marcador({ C, numero, estado }) {
  const feito = estado === "feito";
  const destaque = estado === "atual";
  return (
    <div style={{
      width: 34, height: 34, borderRadius: 999, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 15, fontWeight: 800,
      background: feito ? C.success : destaque ? C.brand : C.card,
      color: feito || destaque ? "#fff" : estado === "rotina" ? C.brand : C.muted,
      border: feito || destaque ? "none" : `2px solid ${estado === "rotina" ? C.brand : C.border}`,
    }}>
      {feito ? <Check size={18} strokeWidth={3} /> : numero}
    </div>
  );
}

export default function BoasVindasEstoqueView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const etapas = etapasVisiveis(getRoleFromToken());
  const [info, setInfo] = useState(null);
  const [abertas, setAbertas] = useState({});

  useEffect(() => {
    Promise.all([getEstoqueContexto(), getEstoqueAbertura(), getEstoqueFornecedores(true)])
      .then(([contexto, ab, fornecedores]) => setInfo({ contexto, abertura: ab.abertura, fornecedores: fornecedores.length }))
      .catch(() => setInfo(null));
  }, []);

  const sit = situacoes(info);
  const atual = etapas.find((e) => sit[e.id]?.estado === "atual")?.id || (info?.contexto.abertura_concluida ? "movimentar" : "preparar");
  const estaAberta = (id) => (id in abertas ? abertas[id] : id === atual);

  return (
    <EstoqueLayout titulo="Boas-vindas" isLight={isLight} setIsLight={setIsLight}>
      <div style={{ maxWidth: 820 }}>
        <div style={{ background: "linear-gradient(to right, #581c87, #9d174d)", color: "#fff", borderRadius: 16, padding: isMobile ? 18 : 24, marginBottom: 20 }}>
          <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 800 }}>Boas-vindas à Gestão de Estoque</h1>
          <p style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.5, opacity: 0.9, maxWidth: 620 }}>
            Controle dos materiais de cozinha e limpeza da sede: quanto entrou, quanto saiu e quanto sobrou — em unidades e em reais.
            Siga as etapas abaixo; o menu ao lado segue a mesma ordem.
          </p>
          {info && (
            <button onClick={() => navigate(etapas.find((e) => e.id === atual)?.telas[0].path || "/estoque/posicao")}
              style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", color: "#581c87", border: "none", borderRadius: 10, padding: "10px 16px", minHeight: 44, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
              {info.contexto.abertura_concluida ? "Ir para a rotina" : "Continuar de onde parei"} <ArrowRight size={16} />
            </button>
          )}
        </div>

        <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {etapas.map((etapa, idx) => {
            const s = sit[etapa.id] || { estado: "pendente", texto: "" };
            const aberta = estaAberta(etapa.id);
            const ultima = idx === etapas.length - 1;
            return (
              <li key={etapa.id} style={{ display: "flex", gap: 14 }}>
                {/* trilho da linha do tempo */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Marcador C={C} numero={etapa.numero} estado={s.estado} />
                  {!ultima && <div style={{ width: 2, flex: 1, minHeight: 16, background: s.estado === "feito" ? C.success : C.border, margin: "4px 0" }} />}
                </div>

                <div style={{ flex: 1, minWidth: 0, paddingBottom: ultima ? 0 : 16 }}>
                  <button onClick={() => setAbertas((a) => ({ ...a, [etapa.id]: !aberta }))} aria-expanded={aberta}
                    style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%", background: "none", border: "none", padding: "4px 0 0", cursor: "pointer", fontFamily: "inherit", textAlign: "left", color: C.text }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 16, fontWeight: 800 }}>{etapa.titulo}</span>
                        {s.texto && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 4,
                            background: s.estado === "feito" ? `${C.success}1F` : s.estado === "atual" ? `${C.brand}1F` : C.bgActive,
                            color: s.estado === "feito" ? C.success : s.estado === "pendente" ? C.muted : C.brand }}>
                            {s.estado === "rotina" && <RefreshCw size={11} />} {s.texto}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{etapa.resumo}</div>
                    </div>
                    {aberta ? <ChevronDown size={18} color={C.muted} /> : <ChevronRight size={18} color={C.muted} />}
                  </button>

                  {aberta && (
                    <div style={{ background: C.card, border: `1px solid ${s.estado === "atual" ? C.brand : C.border}`, borderRadius: 12, padding: 14, marginTop: 10 }}>
                      <ol style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 8 }}>
                        {etapa.passos.map((p) => <li key={p} style={{ fontSize: 13, lineHeight: 1.5, color: C.text }}>{p}</li>)}
                      </ol>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                        {etapa.telas.map((t, i) => {
                          const Icon = t.icon;
                          const principal = i === 0;
                          return (
                            <button key={t.id} onClick={() => navigate(t.path)}
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, minHeight: 42, padding: "9px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                                background: principal ? C.brand : "transparent", color: principal ? "#fff" : C.brand, border: principal ? "none" : `1px solid ${C.brand}` }}>
                              <Icon size={16} /> {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </EstoqueLayout>
  );
}
